import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Order, Session, SessionForm, SessionQueue } from '@crowdctrl/core';

export interface PublicFormContextResponse {
  tenantId: string;
  workspaceId: string;
  sessionId: string;
  formId: string;
  form: SessionForm;
  session: Session & { workspace?: { name?: string } };
  queueControls: SessionQueue[];
}

export interface PublicOrderFollowContextResponse {
  tenantId: string;
  workspaceId: string;
  sessionId: string;
  orderId: string;
  order: Order;
}

function buildIntakeUrl(segment: 'verify' | 'context' | 'orders'): string {
  const base = (environment.apiUrl ?? '').replace(/\/$/, '');
  if (!base) {
    throw new Error('API URL is not configured');
  }
  const url = new URL(`${base}/public_form_intake/api/public-form/${segment}`);
  url.searchParams.set('ngsw-bypass', 'true');
  return url.toString();
}

function buildOrderFollowIntakeUrl(sub: 'verify' | 'context'): string {
  const base = (environment.apiUrl ?? '').replace(/\/$/, '');
  if (!base) {
    throw new Error('API URL is not configured');
  }
  const url = new URL(`${base}/public_form_intake/api/public-form/order-follow/${sub}`);
  url.searchParams.set('ngsw-bypass', 'true');
  return url.toString();
}

function parseErrorMessage(raw: string, status: number): string {
  try {
    const data = JSON.parse(raw) as { error?: { message?: string } };
    return data?.error?.message ?? `Request failed (${status})`;
  } catch {
    return `Request failed (${status})`;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PublicFormIntakeService {
  /**
   * Route guard: confirms short link + session form control exist and are enabled (server Admin SDK, no reCAPTCHA).
   */
  async verifyShortUrl(shortUrlDocId: string): Promise<void> {
    const url = buildIntakeUrl('verify');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shortUrlDocId }),
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, res.status));
    }
  }

  async fetchContext(
    shortUrlDocId: string,
    recaptchaToken: string,
  ): Promise<PublicFormContextResponse> {
    const url = buildIntakeUrl('context');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shortUrlDocId, recaptchaToken }),
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, res.status));
    }
    const data = JSON.parse(text) as PublicFormContextResponse;
    return data;
  }

  async verifyOrderFollow(followUrlDocId: string): Promise<void> {
    const url = buildOrderFollowIntakeUrl('verify');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ followUrlDocId }),
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, res.status));
    }
  }

  async fetchOrderFollowContext(
    followUrlDocId: string,
    recaptchaToken: string,
  ): Promise<PublicOrderFollowContextResponse> {
    const url = buildOrderFollowIntakeUrl('context');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ followUrlDocId, recaptchaToken }),
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, res.status));
    }
    return JSON.parse(text) as PublicOrderFollowContextResponse;
  }

  async createOrder(body: {
    shortUrlDocId: string;
    recaptchaToken: string;
    workspaceId: string;
    sessionId: string;
    formId: string;
    queueId: string;
    summary: string;
    customerIdentifier: string;
    sourceType: string;
    sourceId: string;
    sourceMetadata?: Record<string, string>;
  }): Promise<{ order: Order }> {
    const url = buildIntakeUrl('orders');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, res.status));
    }
    const data = JSON.parse(text) as { order: Order };
    if (!data?.order?.id) {
      throw new Error('Invalid response from server');
    }
    return data;
  }
}
